/**
 * Координаты для подмены гео — копия листа «Координаты» из гугл-таблицы,
 * колонка на страну. Клик по паре — копирование в буфер; галочка помечает
 * «использована» (блёкнет, но остаётся); в шапке колонки — добавление новых
 * (одной парой или простынёй из буфера).
 */
import { useEffect, useMemo, useState } from 'react';
import type { User } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { ModalPortal } from '../ui/ModalPortal.js';
import { CoordinatesTable, type Coord, type CountryBlock } from '../ui/CoordinatesTable.js';

function AddModal({ country, countries, onClose, onDone }: {
  country: string | null;
  countries: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(country ?? '');
  const [bulk, setBulk] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const preview = useMemo(
    () => bulk.split('\n').filter((l) => /^\s*-?\d+(?:[.,]\d+)?\s*[,;\s]\s*-?\d+(?:[.,]\d+)?\s*$/.test(l)).length,
    [bulk],
  );

  async function submit() {
    if (!name.trim() || preview === 0 || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ added: number; dupes: number }>('/api/coordinates', {
        method: 'POST',
        body: JSON.stringify({ country: name.trim(), bulk }),
      });
      onDone();
      onClose();
      if (r.dupes) alert(`Добавлено ${r.added}, пропущено дублей: ${r.dupes}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не получилось');
      setBusy(false);
    }
  }

  return (
    <ModalPortal>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
        <div className="modal-panel" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-title">Добавить координаты</div>
          <div style={{ display: 'grid', gap: 10 }}>
            <label className="field" style={{ margin: 0 }}>
              <span>Страна</span>
              <input
                className="admin-input"
                list="coord-countries"
                placeholder="Польша"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <datalist id="coord-countries">
                {countries.map((c) => <option key={c} value={c} />)}
              </datalist>
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span>Координаты — по паре в строке</span>
              <textarea
                className="admin-input"
                rows={8}
                placeholder={'51.16438 16.80438\n51.36538, 17.20638'}
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
                style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, resize: 'vertical' }}
              />
            </label>
            <div style={{ fontSize: 12.5, color: preview ? 'var(--text-muted)' : 'var(--text-subtle)' }}>
              распознано пар: <b>{preview}</b> · разделитель — пробел, запятая или «;»
            </div>
            {err && <div className="error">{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={onClose} disabled={busy}>Отмена</button>
              <button
                className="btn-primary"
                style={{ width: 'auto', padding: '0 20px' }}
                disabled={busy || !name.trim() || preview === 0}
                onClick={submit}
              >
                {busy ? '…' : `Добавить ${preview || ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export function CoordinatesPage({ me }: { me: User }) {
  const [blocks, setBlocks] = useState<CountryBlock[] | null>(null);
  const [adding, setAdding] = useState<{ country: string | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hideUsed, setHideUsed] = useState(false);

  function load() {
    api<{ countries: CountryBlock[] }>('/api/coordinates')
      .then((r) => setBlocks(r.countries))
      .catch(() => setBlocks([]));
  }
  useEffect(load, []);

  if (!blocks) return <div className="empty-state">Загрузка…</div>;

  const total = blocks.reduce((s, b) => s + b.items.length, 0);
  const used = blocks.reduce((s, b) => s + b.items.filter((i) => i.usedAt).length, 0);

  return (
    <div className="page-inner">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>Координаты</h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            {total} координат в {blocks.length} странах · использовано {used} · как гугл-лист: выделение, Ctrl+C/V, Del, Cmd+Z, двойной клик — правка
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={hideUsed} onChange={(e) => setHideUsed(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
            Скрыть использованные
          </label>
          <button className="btn-primary" style={{ width: 'auto', padding: '9px 18px' }} onClick={() => setAdding({ country: null })}>
            + Добавить координаты
          </button>
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="empty-state">Координат пока нет — добавь первые.</div>
      ) : (
        <CoordinatesTable
          blocks={blocks}
          hideUsed={hideUsed}
          onReload={load}
          onError={(m) => setErr(m)}
          onAddColumn={() => setAdding({ country: null })}
        />
      )}

      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}

      {adding && (
        <AddModal
          country={adding.country}
          countries={blocks.map((b) => b.country)}
          onClose={() => setAdding(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
